
import { defineComponent, onMounted, reactive, watch } from 'vue'

// @ui-kit
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'

// @types
import { Courier } from '@/new-services/types/couriers'
import { Sector } from '@/new-services/types/sectors'
import { Wave } from '@/new-services/types/waves'

// @new-services
import couriersAPI from '@/new-services/couriers.service'
import sectorsAPI from '@/new-services/sectors.service'
import wavesAPI from '@/new-services/waves.service'

// composable
import useNotifications from '@/common/composable/useNotifications'

// @store
import { useNewDispatcherStore } from '@/modules/NewDispatcher/stores/useNewDispatcherStore'

export default defineComponent({
    components: {
        NewInput,
        NewSelect,
        NewButton,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const new_dispatcher_store = useNewDispatcherStore()
        const notifications = useNotifications()

        const state = reactive({
            fromDate: null as string | null,
            toDate: null as string | null,
            courierId: null as number | null,
            invoiceNumber: null as string | null,
            number: null as string | null,
            sectorId: null as number | null,
            waveId: null as number | null,
        })

        const onFiltersChanged = () => {
            if (props.disabled || props.loading) return
            emit('submit', { ...state })
        }

        const onFiltersReset = () => {
            state.fromDate = null
            state.toDate = null
            state.invoiceNumber = null
            state.number = null
            state.courierId = null
            state.sectorId = null
            state.waveId = null

            emit('reset')
        }

        const data_state = reactive({
            couriers: {
                is_loading: false,
                is_failed: false,
                list: [] as Courier[],
            },
            sectors: {
                is_loading: false,
                is_failed: false,
                list: [] as Sector[],
            },
            waves: {
                is_loading: false,
                is_failed: false,
                list: [] as Wave[],
            },
        })

        const loadCouriers = async (dispatcher_sector_ids: number[]) => {
            try {
                data_state.couriers.is_loading = true

                const response = await couriersAPI.getCouriers({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds: dispatcher_sector_ids,
                    statusIds: [2],
                    typesIds: [1],
                })
                data_state.couriers.list = response.data.data

                data_state.couriers.is_failed = false
            } catch (error) {
                data_state.couriers.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка курьеров произошла ошибка',
                })
            } finally {
                data_state.couriers.is_loading = false
            }
        }

        const loadSectors = async (dispatcher_sector_ids: number[]) => {
            try {
                data_state.sectors.is_loading = true

                const response = await sectorsAPI.getSectors({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds: dispatcher_sector_ids,
                })
                data_state.sectors.list = response.data.data

                data_state.sectors.is_failed = false
            } catch (error) {
                data_state.sectors.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка секторов произошла ошибка',
                })
            } finally {
                data_state.sectors.is_loading = false
            }
        }

        const loadWaves = async (dispatcher_sector_ids: number[]) => {
            try {
                data_state.waves.is_loading = true

                const response = await wavesAPI.getWaves({
                    dispatcherSectorIds: dispatcher_sector_ids,
                })
                data_state.waves.list = response.data.data

                data_state.waves.is_failed = false
            } catch (error) {
                data_state.waves.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка волн произошла ошибка',
                })
            } finally {
                data_state.waves.is_loading = false
            }
        }

        const preloadComponentData = () => {
            new_dispatcher_store.validation$.$touch()
            if (new_dispatcher_store.validation$.$invalid) return

            loadCouriers(new_dispatcher_store.dispatcherSectorIds)
            loadSectors(new_dispatcher_store.dispatcherSectorIds)
            loadWaves(new_dispatcher_store.dispatcherSectorIds)
        }

        onMounted(preloadComponentData)

        // New Dispatcher Sector Listener
        watch(
            () => new_dispatcher_store.dispatcherSectorIds,
            () => {
                onFiltersReset()
                preloadComponentData()
            }
        )

        return {
            state,
            onFiltersChanged,
            onFiltersReset,
            //
            data_state,
            loadCouriers,
            loadSectors,
            loadWaves,
        }
    },
})
